<template>
  <div>
    <v-card>
      <v-data-table
        :headers="headers"
        :items="workplaceAssignations"
        :items-per-page="15"
        :footer-props="{
          itemsPerPageOptions: [15, 30, -1],
          itemsPerPageText: $t('table.itemsPerPageText')
        }"
        sort-by="name"
        class="table-cursor"
      >
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="700px">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  dark
                  class="mb-2"
                  v-bind="attrs"
                  v-on="on"
                  >{{ $t("button.newAssignation") }}</v-btn
                >
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-container>
                    <v-form ref="form">
                      <v-row>
                        <v-col cols="12" md="8">
                          <v-autocomplete
                            v-model="laboratorySelected"
                            :items="workplaces"
                            item-text="name"
                            item-value="name"
                            clearable
                            hide-no-data
                            cache-items
                            @change="editedItem.workplace = null"
                            :label="$t('form.workplace.name')"
                            :rules="rules.fieldRequired"
                          >
                        </v-autocomplete>
                        </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <v-autocomplete
                                    v-model="editedItem.workplace"
                                    :items="workplaces.filter(item => item.name === laboratorySelected)"
                                    item-text="place_code"
                                    item-value="id"
                                    :return-object="false"
                                    :label="$t('forms.building')"
                                    :rules="rules.fieldRequired"
                                    :disabled="laboratorySelected === ''"
                                    clearable
                                    hide-no-data
                                    cache-items
                            >
                            <template v-slot:item="data">
                              <div class="d-flex align-center fill-parent">
                                <v-tooltip 
                                  left
                                  open-delay="50"
                                  transition="fade-transition"
                                  content-class="custom-tooltip"
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <div 
                                      class="flex-grow-1 fill-parent"
                                      v-bind="attrs"
                                      v-on="on"
                                    >
                                        {{ data.item.place_code }}
                                    </div>
                                  </template>
                                  <v-card>
                                    <img
                                      :src="getWorkplaceImagePath(data.item.place_code)"
                                      contain
                                      height="200"
                                      width="200"
                                    />
                                  </v-card>
                                </v-tooltip>
                              </div>
                            </template>
                          </v-autocomplete>
                          </v-col>
                        <v-col cols="12" sm="6" md="6">
                          <div class="expiration-date-with-actions">
                            <v-menu
                                    v-model="menu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="290px"
                            >
                              <template v-slot:activator="{ on }">
                                <v-text-field
                                        v-model="editedItem.expiration_date"
                                        :label="$t('form.expiration_date')"
                                        prepend-inner-icon="event"
                                        readonly
                                        v-on="on"
                                        :rules="rules.fieldRequired"
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                      v-model="editedItem.expiration_date"
                                      @input="menu = false"
                              ></v-date-picker>
                            </v-menu>
                            <v-menu offset-y>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  small
                                  class="ml-1 mt-2"
                                  :disabled="!vinculationEndDate"
                                  v-bind="attrs"
                                  v-on="on"
                                >
                                  <v-icon>mdi-calendar-import</v-icon>
                                </v-btn>
                              </template>
                              <v-list dense>
                                <v-list-item @click="copyVinculationEndDateToWorkplace">
                                  <v-list-item-title>
                                    {{ $t("button.copyVinculationEndDate") }}
                                  </v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="6" md="6">
                          <v-checkbox
                            v-model="editedItem.freed_status"
                            :label="$t('table.freedWorkplace')"
                          />
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="close">{{ $t("button.cancel") }}</v-btn>
                  <v-btn color="primary" @click="save">{{
                    $t("button.save")
                  }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.workplaceName="{ item }">
          <span v-if="workplaces.length > 0">{{ getNameWorkplace(item.workplace) }}</span>
          <v-skeleton-loader
            v-else
            type="text"
            max-width="300"
          ></v-skeleton-loader>
        </template>
        <template v-slot:item.expiration_date="{ item }">
          <span>
            {{
              new Date(item.expiration_date).toISOString().substr(0, 10)
            }}</span
          >
        </template>
        <template v-slot:item.freed_status="{ item }">
          <v-icon class="ml-6" v-if="item.freed_status" color="green">
            mdi-check
          </v-icon>
          <v-icon v-else class="ml-6" color="grey">
            mdi-close
          </v-icon>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            :disabled="workplaces.length===0"
            icon
            text
            class="mr-2"
            color="accent"
            :to="'/building#' + getCodeWorkplace(item.workplace)"
          >
            <v-icon>mdi-floor-plan</v-icon>
          </v-btn>
          <v-icon class="mr-2" color="primary" @click="editItem(item)" :disabled="workplaces.length===0">
            mdi-pencil
          </v-icon>
          <v-icon color="red" @click="deleteItem(item)" :disabled="workplaces.length===0">
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import validationRules from "../../../mixins/validationRules";
import investigator_model from "../../../mixins/model_utils/investigator_model";
export default {
  mixins: [validationRules, investigator_model],
  props: {
    person: String,
    vinculationEndDate: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      menu: false,
      dialog: false,
      search: "",
      laboratorySelected: "",
      workplaceAssignations: [],
      editedIndex: -1,
      editedItem: {
        workplace: null,
        expiration_date: null,
        freed_status: false
      },
      defaultItem: {
        workplace: null,
        expiration_date: null,
        freed_status: false
      }
    };
  },

  watch: {
    dialog(val) {
      val || this.close();
    }
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("button.newAssignation")
        : this.$t("title.editAssignation");
    },
    headers() {
      return [
        {
          text: this.$t("forms.building"),
          value: "workplaceName"
        },
        {
          text: this.$t("form.expiration_date"),
          value: "expiration_date",
          width: "20%"
        },
        {
          text: this.$t("form.freedWorkplace"),
          value: "freed_status",
          width: "20%"
        },
        {
          text: this.$t("actions"),
          value: "actions",
          width: "15%",
          align: "end",
          filterable: false,
          sortable: false
        }
      ];
    }
  },
  created() {
    this.getWorkplaces();
    this.initialize();
  },

  methods: {
    initialize() {
      let path = this.$store.state.endpoint + "/workplaceAssignation";
      axios
        .get(path)
        .then(response => {
          this.workplaceAssignations = response.data.filter(item => item.person.toString() === this.person);
        })
        .catch(error => {
          console.log(error);
        });
    },

    getNameWorkplace(id) {
      let workplace = this.workplaces.find(workplace => workplace.id === id);
      if (workplace !== undefined) {
        return workplace.name + " - " + workplace.place_code;
      }
      return "";
    },

    getCodeWorkplace(id) {
      let workplace = this.workplaces.find(workplace => workplace.id === id);
      if (workplace !== undefined) {
        return workplace.place_code.toLowerCase().replace('-', '_');
      }
      return "";
    },

    getWorkplaceImagePath(code){
      const filename = `mesa_${code.toLowerCase().replace('-', '_').replace('ñ','nh')}.png`;
      try {
        return require(`@/assets/building/${filename}`);
      }
      catch (e) {
        console.log(`Picture for workplace not found: ${filename}`);
        return require("@/assets/building/default.png");
      }
    },

    copyVinculationEndDateToWorkplace() {
      if (this.vinculationEndDate) {
        this.editedItem.expiration_date = this.vinculationEndDate;
      }
    },

    editItem(item) {
      this.editedIndex = this.workplaceAssignations.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.editedItem.id = item.id;
      this.laboratorySelected = this.workplaces.find(workplace => workplace.id === item.workplace).name
      this.dialog = true;
    },

    deleteItem(item) {
      if (confirm(this.$t("message.confirm.delete"))) {
        let path =
          this.$store.state.endpoint + "/workplaceAssignation/" + item.id;
        axios
          .delete(path)
          .then(response => {
            const index = this.workplaceAssignations.indexOf(item);
            this.workplaceAssignations.splice(index, 1);
            this.$store.commit("alert", {
              color: "success",
              text: this.$t("message.correct.deleteItem")
            });
          })
          .catch(error => {
            console.log(error);
            this.$store.commit("alert", {
              color: "error",
              text: error.response
            });
          });
      }
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.$refs.form.reset();
      });
    },

    save() {
      if (this.$refs.form.validate()) {
        let path;
        const index = this.editedIndex;
        const data = this.editedItem;
        if (index > -1) {
          path =
            this.$store.state.endpoint +
            "/workplaceAssignation/" +
            this.editedItem.id;
          axios
            .patch(path, this.editedItem)
            .then(response => {
              Object.assign(this.workplaceAssignations[index], data);
              this.$store.commit("alert", {
                color: "success",
                text: this.$t("message.correct.updateItem")
              });
            })
            .catch(error => {
              console.log(error);
              this.$store.commit("alert", {
                color: "error",
                text: error.response.data
              });
            });
        } else {
          path = this.$store.state.endpoint + "/workplaceAssignation";
          const data = this.editedItem;
          data['person'] = this.person;
          axios
            .post(path, data)
            .then(response => {
              data.id = response.data.id;
              this.workplaceAssignations.push(data);
              this.$store.commit("alert", {
                color: "success",
                text: this.$t("message.correct.createItem")
              });
            })
            .catch(error => {
              console.log(error);
              this.$store.commit("alert", {
                color: "error",
                text: error.response.data
              });
            });
        }
        this.close();
      }
    }
  }
};
</script>

<style scoped>
.fill-parent {
  line-height: 3;
  width: 100%;
}
.custom-tooltip {
  background-color: rgba(250, 250, 250, 0.9);
  padding: 5px;
  transform: translateX(-15px);
}
.expiration-date-with-actions {
  display: flex;
  align-items: flex-start;
}
.expiration-date-with-actions .v-menu {
  flex: 1 1 auto;
}
</style>
